import {
  ENTITY_CLASS,
  type FacilityKey,
  type Organization,
  type OrganizationKey,
  type OrganizationQuery,
  type UserKey,
  isOrganizationKey,
  streamError,
} from "@packages/core";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

import { fetchData, findOrganizations } from "./functions";
import { QueryKey } from "./query";
import { NETWORK_SEMAPHORE } from "./semaphore";
import { TANSTACK_QUERY_CLIENT } from "./tanstack";

// =============================================================
// 組織の単体取得
// =============================================================

/**
 * 組織を取得する。存在しない場合、`null`を返す。
 * ユーザー名が組織キーになっている場合の取得に使用し易いように、
 * 施設キーやユーザーキーの指定出来る。その場合、`undefined`を返す。
 * @param organizationKey 組織キー
 * @returns 組織
 */
export async function findOrganization(
  organizationKey: FacilityKey | OrganizationKey | UserKey | false | null | undefined,
) {
  if (!isOrganizationKey(organizationKey)) {
    return undefined;
  }
  const { output } = await fetchData({ type: "Entity::Organization", input: { organizationKey } });
  if (output instanceof Error) {
    throw output;
  } else {
    return output;
  }
}

function OrganizationQueryKey(
  organizationKey: FacilityKey | OrganizationKey | UserKey | false | null | undefined,
) {
  return QueryKey(ENTITY_CLASS.ORGANIZATION, organizationKey);
}

export function updateOrganizationCache(organization: Organization) {
  TANSTACK_QUERY_CLIENT.setQueryData(
    OrganizationQueryKey(organization.organizationKey),
    organization,
  );
}

export function useOrganizationQuery(
  organizationKey: FacilityKey | OrganizationKey | UserKey | false | null | undefined,
) {
  return useQuery({
    queryKey: OrganizationQueryKey(organizationKey),
    async queryFn() {
      return findOrganization(organizationKey);
    },

    enabled: isOrganizationKey(organizationKey),
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: /** 4時間 */ 14_400_000,
  });
}

// =============================================================
// 組織の一覧
// =============================================================

function OrganizationInfiniteQueryKey(...parameters: ReadonlyArray<any>) {
  return QueryKey(ENTITY_CLASS.ORGANIZATION + "List", ...parameters);
}

export function removeOrganizationsCache() {
  TANSTACK_QUERY_CLIENT.invalidateQueries({
    queryKey: OrganizationInfiniteQueryKey().slice(0, 1),
  });
}

export function useListedOrganizations(
  query: OrganizationQuery,
  options?: Partial<{
    /** 処理を実行する場合に`true`を設定するフラグ */
    enabled: boolean;
  }>,
) {
  const { data, error, fetchNextPage, hasNextPage, isLoading } = useInfiniteQuery({
    queryKey: OrganizationInfiniteQueryKey(query, options),
    async queryFn(context) {
      const { organizations, numberOfErrors, nextPageToken } = await findOrganizations({
        ...query,
        ...context.pageParam,
      });

      for (const organization of organizations) {
        updateOrganizationCache(organization);
      }

      return { organizations, numberOfErrors, nextPageToken };
    },

    getNextPageParam(lastPage) {
      return lastPage.nextPageToken ? { nextPageToken: lastPage.nextPageToken } : undefined;
    },
    initialPageParam: {} as { nextPageToken?: Record<string, any> },

    enabled: options?.enabled,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
  });

  useEffect(
    function () {
      if (error) {
        streamError(error);
      }
    },
    [error],
  );

  const organizations = useMemo(
    function () {
      return (
        data?.pages.flatMap(function (page) {
          return page.organizations;
        }) ?? []
      );
    },
    [data?.pages],
  );

  return {
    error,
    fetchNextPage,
    loaded: !hasNextPage,
    loading: isLoading,
    organizations,
    pages: data?.pages ?? [],
  };
}

export function removeOrganizationInfiniteQueryCache() {
  TANSTACK_QUERY_CLIENT.invalidateQueries({
    queryKey: OrganizationInfiniteQueryKey().slice(0, 1),
  });
}

export function useOrganizationInfiniteQuery(
  query: OrganizationQuery,
  options?: Partial<{
    /** 処理を実行する場合に`true`を設定するフラグ */
    enabled: boolean;
  }>,
) {
  return useInfiniteQuery({
    queryKey: OrganizationInfiniteQueryKey(query, options),
    async queryFn(context) {
      const release = await NETWORK_SEMAPHORE.acquire();
      try {
        const { organizations, numberOfErrors, nextPageToken } = await findOrganizations({
          ...query,
          ...context.pageParam,
        });

        // for (const organization of organizations) {
        //   updateOrganizationQueryData(organization.organizationKey, organization);
        // }

        return { organizations, numberOfErrors, nextPageToken };
      } finally {
        release();
      }
    },

    getNextPageParam(lastPage) {
      return lastPage.nextPageToken ? { nextPageToken: lastPage.nextPageToken } : undefined;
    },
    initialPageParam: {} as { nextPageToken?: Record<string, any> },

    enabled: options?.enabled,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: /** 4時間 */ 14_400_000,
  });
}
